/* AdminMgmtCommon — shared mini-components for the new Mgmt Hub pages.
 *
 * Matches the style of AdminOperations / AdminSecurity / AdminFinance:
 *   • Kpi tile
 *   • SectionHeader with refresh button
 *   • DataTable (thin wrapper)
 *   • ReasonInput (audit-logged reason capture)
 */
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export function MgmtKpi({ label, value, tone = "neutral", hint, testid }) {
  const tones = {
    neutral: "bg-foreground/3 border-foreground/10",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
    ink:     "bg-foreground text-background border-foreground",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] opacity-70 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {hint && <div className="text-[10.5px] opacity-65 mt-0.5">{hint}</div>}
    </div>
  );
}

export function MgmtSectionHeader({ title, subtitle, onRefresh, busy, action }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
      <div>
        <div className="text-[13.5px] font-semibold">{title}</div>
        {subtitle && <div className="text-[11.5px] text-foreground/55 mt-0.5">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-2">
        {action}
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={busy} className="h-8" data-testid="mgmt-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
          </Button>
        )}
      </div>
    </div>
  );
}

export function MgmtEmpty({ message = "No data yet." }) {
  return (
    <div className="px-3 py-10 text-center text-foreground/45 text-sm">{message}</div>
  );
}

export function ReasonField({ value, onChange, placeholder = "Reason (min 3 chars · audit-logged)", testid = "mgmt-reason" }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-foreground/55">Reason *</Label>
      <Textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} className="text-[13px]" data-testid={testid}/>
    </div>
  );
}

export const TABS_STYLE = "flex gap-1 border-b overflow-x-auto";
export function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 whitespace-nowrap ${
        active ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground/80"
      }`}>
      {Icon && <Icon className="w-3.5 h-3.5"/>}{label}
    </button>
  );
}
